import { useCallback } from 'react';

import { leadsApi } from '@/services/api';
import { getAttributionDataWithFreshFbp } from '@/utils/attributionTracking';
import { getLeadJourneyData } from '@/utils/leadJourneyTracking';

const isIOSSafari = (): boolean => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
};

interface WhatsAppClickOptions {
  includeJourney?: boolean;
  prefillMessage?: string;
}

export const useWhatsAppClick = (
  sourceLocation: string,
  { includeJourney = true, prefillMessage = '' }: WhatsAppClickOptions = {}
): (() => void) => {
  const handleClick = useCallback(() => {
    const base = 'https://wa.me/6580127012';
    const whatsAppUrl = prefillMessage
      ? `${base}?text=${encodeURIComponent(prefillMessage)}`
      : base;

    // iOS Safari: Use window.location.href directly for better deep link handling
    // Other platforms: Use window.open() to open in new tab
    // Guard navigation errors so analytics still fire (e.g. blocked popup, test env).
    try {
      if (isIOSSafari()) {
        window.location.href = whatsAppUrl;
      } else {
        window.open(whatsAppUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (navigationError) {
      if (import.meta.env.DEV) {
        console.warn('Failed to open WhatsApp link:', navigationError);
      }
    }

    // Track click asynchronously after opening (fire-and-forget)
    // This doesn't block the WhatsApp link from opening
    (async () => {
      try {
        // Get attribution data - wrap in try-catch to ensure it never blocks tracking

        let attributionData: any = { firstTouch: null, lastTouch: null };
        try {
          attributionData = getAttributionDataWithFreshFbp();
        } catch (attributionError) {
          // Non-critical: log but continue with empty attribution
          console.warn(
            'Failed to get attribution data for WhatsApp click:',
            attributionError
          );
        }

        let journeyData: Record<string, unknown> | null = null;
        if (includeJourney) {
          try {
            journeyData = getLeadJourneyData() as Record<
              string,
              unknown
            > | null;
          } catch (journeyError) {
            console.warn(
              'Failed to get lead journey data for WhatsApp click:',
              journeyError
            );
          }
        }

        const pageUrl = window.location.href;
        await leadsApi.trackWhatsAppClick(
          attributionData,
          pageUrl,
          sourceLocation,
          includeJourney ? journeyData : null
        );
      } catch (error) {
        // Log error but don't block the WhatsApp link from opening
        if (import.meta.env.DEV) {
          console.error('Failed to track WhatsApp click:', error);
        }
      }
    })();
  }, [includeJourney, prefillMessage, sourceLocation]);

  return handleClick;
};
